import { supabase } from './supabase'

export {
  achievementHigherIsBetter,
  achievementLowerIsBetter,
  attentionMetric,
  evaluateResult,
  metricStatus,
  participationTargetResponses,
  strongestMetric,
} from './performanceKpis'
export type { MetricEvaluation, PerformanceMetric, PerformanceStatus } from './performanceKpis'

export type PerformanceImport = {
  id: number
  competence: string
  source_filename: string
  imported_by: string
  imported_at: string
  row_count: number
  employee_count: number
  multi_unit_employee_count: number
  registered_login_count: number
  unregistered_login_count: number
  checksum: string
  status: 'publicado'
}

export type PerformanceTarget = {
  id: number
  effective_from: string
  daily_attendance_target: number
  tma_target_seconds: number
  tma_low_alert_seconds: number
  nps_target: number
  nps_participation_target_pct: number
  created_at: string
}

export type PerformanceMonthlyResult = {
  id: number
  import_id: number
  competence: string
  login_normalized: string
  employee_name: string
  units: string[]
  unit_count: number
  total_attendances: number
  total_worked_days: number
  daily_average: number | null
  tma_seconds: number | null
  nps_score: number | null
  nps_responses: number
  nps_participation_pct: number | null
  attending_pct: number | null
  idle_pct: number | null
  productive_idle_pct: number | null
  unproductive_idle_pct: number | null
  absent_seconds: number | null
  logged_seconds: number | null
  extra_time_seconds: number | null
  target_id: number
  target_daily_attendance: number
  target_tma_seconds: number
  target_tma_low_alert_seconds: number
  target_nps: number
  target_nps_participation_pct: number
  benchmark_daily_average: number | null
  benchmark_tma_seconds: number | null
  benchmark_nps_score: number | null
  benchmark_nps_participation_pct: number | null
}

export function normalizeLogin(value: string) {
  return value.trim().toLowerCase()
}

export function competenceLabel(value: string) {
  const date = new Date(`${value.slice(0, 7)}-01T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' })
    .format(date)
    .replace(/^./, (letter) => letter.toUpperCase())
}

export function formatNumber(value: number | null | undefined, digits = 2) {
  if (value == null || !Number.isFinite(Number(value))) return '—'
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(Number(value))
}

export function formatPercent(value: number | null | undefined, digits = 1) {
  if (value == null || !Number.isFinite(Number(value))) return '—'
  return `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(Number(value))}%`
}

export function formatDuration(totalSeconds: number | null | undefined) {
  if (totalSeconds == null || !Number.isFinite(Number(totalSeconds))) return '—'
  const rounded = Math.max(0, Math.round(Number(totalSeconds)))
  const hours = Math.floor(rounded / 3600)
  const minutes = Math.floor((rounded % 3600) / 60)
  const seconds = rounded % 60
  return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':')
}

export function formatDurationDifference(totalSeconds: number) {
  const rounded = Math.max(0, Math.round(Math.abs(totalSeconds)))
  const minutes = Math.floor(rounded / 60)
  const seconds = rounded % 60
  if (minutes === 0) return `${seconds}s`
  return `${minutes}min${String(seconds).padStart(2, '0')}s`
}

export async function loadAvailablePerformanceCompetences() {
  const { data, error } = await supabase.rpc('performance_available_competences')
  if (error) throw error
  return ((data ?? []) as { competence: string }[]).map((item) => item.competence)
}

export async function loadMyPerformanceResults() {
  const { data, error } = await supabase
    .from('performance_monthly_results')
    .select('*')
    .order('competence', { ascending: false })
    .limit(3)
  if (error) throw error
  return (data ?? []) as PerformanceMonthlyResult[]
}

export async function loadTeamPerformanceResults(competence: string) {
  const { data, error } = await supabase
    .from('performance_monthly_results')
    .select('*')
    .eq('competence', competence)
    .order('employee_name', { ascending: true })
  if (error) throw error
  return (data ?? []) as PerformanceMonthlyResult[]
}

export async function loadPerformanceImports() {
  const { data, error } = await supabase
    .from('performance_imports')
    .select('*')
    .order('competence', { ascending: false })
    .limit(3)
  if (error) throw error
  return (data ?? []) as PerformanceImport[]
}

export async function loadPerformanceTargets() {
  const { data, error } = await supabase
    .from('performance_targets')
    .select('*')
    .order('effective_from', { ascending: false })
  if (error) throw error
  return (data ?? []) as PerformanceTarget[]
}
